'use client';

import { useRef, useState, useCallback, useEffect } from 'react';
import { useIsNative } from '~/context/common/DeviceContext';
import { TDC_API_BASE } from './tdcApi';

const CODE_LENGTH = 6;

function QrIcon() {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="2" width="8" height="8" rx="1" />
      <rect x="14" y="2" width="8" height="8" rx="1" />
      <rect x="2" y="14" width="8" height="8" rx="1" />
      <rect x="14" y="14" width="4" height="4" rx="0.5" />
      <line x1="22" y1="14" x2="22" y2="22" />
      <line x1="14" y1="22" x2="22" y2="22" />
      <line x1="18" y1="14" x2="18" y2="18" />
      <line x1="14" y1="18" x2="18" y2="18" />
    </svg>
  );
}

export default function TdcPairingScreen() {
  const [error, setError] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);

      return params.get('error') === 'expired'
        ? 'That code has expired. Check the display for a new code.'
        : null;
    }

    return null;
  });
  const isNative = useIsNative();
  const [submitting, setSubmitting] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const getCode = useCallback(() => {
    return inputRefs.current.map(r => r?.value || '').join('');
  }, []);

  const trySubmit = useCallback(async () => {
    const code = getCode();
    if (code.length !== CODE_LENGTH) return;

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${TDC_API_BASE}/pair/${code}?format=json`, {
        headers: { Accept: 'application/json' }
      });
      const data = await res.json();

      if (data.ok && data.session) {
        if (!isNative) window.location.href = `/new?session=${data.session}`;
      } else {
        setError(
          data.error === 'expired'
            ? 'That code has expired. Check the display for a new code.'
            : 'Pairing failed. Please try again.'
        );
        setSubmitting(false);
        inputRefs.current.forEach(r => {
          if (r) r.value = '';
        });
        inputRefs.current[0]?.focus();
      }
    } catch {
      setError('Could not connect to the display. Please try again.');
      setSubmitting(false);
    }
  }, [getCode, isNative]);

  const handleInput = useCallback(
    (idx: number) => (e: React.FormEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      input.value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');

      if (input.value && idx < CODE_LENGTH - 1) {
        inputRefs.current[idx + 1]?.focus();
      }

      trySubmit();
    },
    [trySubmit]
  );

  const handleKeyDown = useCallback(
    (idx: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !e.currentTarget.value && idx > 0) {
        const prev = inputRefs.current[idx - 1];
        if (prev) {
          prev.value = '';
          prev.focus();
        }
      }
    },
    []
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const text = (e.clipboardData.getData('text') || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');

      for (let j = 0; j < CODE_LENGTH; j++) {
        const ref = inputRefs.current[j];
        if (ref) ref.value = text[j] || '';
      }

      const lastIdx = Math.min(text.length, CODE_LENGTH) - 1;
      if (lastIdx >= 0) {
        inputRefs.current[Math.min(lastIdx + 1, CODE_LENGTH - 1)]?.focus();
      }

      trySubmit();
    },
    [trySubmit]
  );

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  return (
    <div
      className="flex min-h-[70vh] flex-col items-center justify-center bg-background-default px-lg text-center"
      data-testid="tdc-pairing-screen"
    >
      <div className="mb-lg text-interactive-default">
        <QrIcon />
      </div>

      <h1 className="t-heading-2xl mb-sm text-text-strong">
        Connect to Display
      </h1>

      <p className="t-body-md mb-xl max-w-[360px] text-text-weak">
        Enter the code shown on the big screen.
      </p>

      {error && (
        <div className="mb-lg rounded-lg border border-status-danger-default bg-status-danger-subtle px-lg py-md text-sm text-status-danger-default">
          {error}
        </div>
      )}

      <div className="mb-md flex gap-sm">
        {Array.from({ length: CODE_LENGTH }, (_, i) => (
          <input
            key={i}
            ref={el => {
              inputRefs.current[i] = el;
            }}
            className="h-[64px] w-[48px] rounded-lg border-2 border-border-default bg-background-muted text-center text-2xl font-bold uppercase text-text-strong outline-none transition-colors focus:border-interactive-default"
            type="text"
            maxLength={1}
            autoComplete="off"
            autoCapitalize="characters"
            inputMode="text"
            disabled={submitting}
            onInput={handleInput(i)}
            onKeyDown={handleKeyDown(i)}
            onPaste={i === 0 ? handlePaste : undefined}
          />
        ))}
      </div>

      {submitting && (
        <p className="t-body-sm mt-lg text-text-weak">Connecting...</p>
      )}
    </div>
  );
}
